/**
 * DYNAMIC LIST/TABLE RENDERER — Renders a data table based on list configuration.
 * Features: search (debounced), sort, pagination, CSV/Excel export, empty/error states.
 */
import React, { useState, useRef, useCallback } from 'react';
import {
  Box, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableSortLabel, TextField, InputAdornment, IconButton, Chip,
  Typography, Stack, Button, Tooltip, Pagination, LinearProgress,
  Menu, MenuItem, Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ClearIcon from '@mui/icons-material/Clear';
import InboxIcon from '@mui/icons-material/Inbox';

export default function DynamicListRenderer({
  moduleKey,
  moduleConfig,
  records = [],
  total = 0,
  page = 1,
  limit = 50,
  loading = false,
  error = null,
  onPageChange,
  onSearch,
  onSort,
  sortBy = 'created_at',
  sortDir = 'desc',
  onView,
  onEdit,
  onDelete,
  onCreate,
  title,
  customActions,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [exportAnchor, setExportAnchor] = useState(null);
  const debounceRef = useRef(null);

  // Build columns from listConfig or fallback to show_in_list fields
  const listColumns = (moduleConfig?.listConfig || [])
    .filter(c => c.is_visible)
    .sort((a, b) => a.sort_order - b.sort_order);

  const columns = listColumns.length > 0
    ? listColumns.map(lc => {
        const field = (moduleConfig?.fields || []).find(f => f.key === lc.field_key);
        return { ...lc, field };
      })
    : (moduleConfig?.fields || [])
        .filter(f => f.show_in_list && f.is_active)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(f => ({
          field_key: f.key,
          label: f.label,
          is_sortable: true,
          is_searchable: true,
          field: f,
        }));

  // Debounced search
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (onSearch) onSearch(value);
    }, 400);
  }, [onSearch]);

  const clearSearch = () => {
    setSearchTerm('');
    if (onSearch) onSearch('');
  };

  const handleSort = (fieldKey) => {
    if (onSort) {
      const newDir = sortBy === fieldKey && sortDir === 'asc' ? 'desc' : 'asc';
      onSort(fieldKey, newDir);
    }
  };

  // ── Cell value formatting ─────────────────────────────────────────────────
  const formatCellValue = (record, col) => {
    const data = record.data || record;
    const value = data[col.field_key];
    const fieldType = col.field?.field_type;

    if (value === null || value === undefined || value === '') return '\u2014';

    switch (fieldType) {
      case 'boolean':
      case 'checkbox':
        return value === true || value === 'true'
          ? <Chip label="Po" size="small" color="success" variant="outlined" />
          : <Chip label="Jo" size="small" color="default" variant="outlined" />;
      case 'date':
        try { return new Date(value + 'T12:00:00').toLocaleDateString('sq-AL'); }
        catch { return value; }
      case 'currency':
        return `${Number(value).toLocaleString('de-DE', { minimumFractionDigits: 2 })} \u20ac`;
      case 'number':
        return Number(value).toLocaleString('de-DE');
      case 'select':
      case 'radio': {
        const opt = (col.field?.options || []).find(o => o.value === value);
        return opt
          ? <Chip label={opt.label} size="small" variant="outlined" sx={opt.color ? { borderColor: opt.color, color: opt.color } : {}} />
          : value;
      }
      case 'multiselect': {
        const vals = Array.isArray(value) ? value : (value || '').split(',').filter(Boolean);
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {vals.map(v => {
              const opt = (col.field?.options || []).find(o => o.value === v);
              return <Chip key={v} label={opt?.label || v} size="small" variant="outlined" />;
            })}
          </Stack>
        );
      }
      case 'email':
        return <Typography variant="body2" sx={{ color: 'primary.main' }}>{value}</Typography>;
      case 'phone':
        return value;
      default:
        return String(value).length > 80 ? String(value).slice(0, 80) + '\u2026' : String(value);
    }
  };

  // Raw value for export
  const rawCellValue = (record, col) => {
    const data = record.data || record;
    const value = data[col.field_key];
    if (value === null || value === undefined) return '';
    if (col.field?.field_type === 'boolean' || col.field?.field_type === 'checkbox') {
      return value === true || value === 'true' ? 'Po' : 'Jo';
    }
    if (col.field?.field_type === 'select' || col.field?.field_type === 'radio') {
      const opt = (col.field?.options || []).find(o => o.value === value);
      return opt ? opt.label : value;
    }
    if (col.field?.field_type === 'multiselect') {
      const vals = Array.isArray(value) ? value : (value || '').split(',').filter(Boolean);
      return vals.map(v => {
        const opt = (col.field?.options || []).find(o => o.value === v);
        return opt?.label || v;
      }).join(', ');
    }
    return String(value);
  };

  // ── Export ────────────────────────────────────────────────────────────────
  const exportCSV = () => {
    setExportAnchor(null);
    const headers = columns.map(c => c.label || c.field?.label || c.field_key);
    const rows = records.map(rec => columns.map(col => {
      const val = rawCellValue(rec, col);
      // Escape CSV: wrap in quotes if contains comma/quote/newline
      if (/[",\n\r]/.test(val)) return `"${val.replace(/"/g, '""')}"`;
      return val;
    }));
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${moduleKey}_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    setExportAnchor(null);
    // Build a simple HTML table that Excel can open
    const headers = columns.map(c => `<th>${c.label || c.field?.label || c.field_key}</th>`).join('');
    const rows = records.map(rec => {
      const cells = columns.map(col => `<td>${rawCellValue(rec, col)}</td>`).join('');
      return `<tr>${cells}</tr>`;
    }).join('');
    const html = `<html><head><meta charset="UTF-8"></head><body><table border="1"><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table></body></html>`;
    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${moduleKey}_export_${new Date().toISOString().slice(0, 10)}.xls`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const pages = Math.ceil(total / limit);

  return (
    <Box>
      {/* Toolbar */}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          {title && <Typography variant="h6" fontWeight={700}>{title}</Typography>}
          <TextField
            size="small"
            placeholder="K\u00ebrko..."
            value={searchTerm}
            onChange={e => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch}><ClearIcon sx={{ fontSize: 16 }} /></IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ width: { xs: '100%', sm: 240 } }}
          />
          {total > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {total} regjistrime
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {records.length > 0 && (
            <>
              <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />}
                onClick={e => setExportAnchor(e.currentTarget)} sx={{ textTransform: 'none' }}>
                Eksporto
              </Button>
              <Menu anchorEl={exportAnchor} open={!!exportAnchor} onClose={() => setExportAnchor(null)}>
                <MenuItem onClick={exportCSV}>Eksporto CSV</MenuItem>
                <MenuItem onClick={exportExcel}>Eksporto Excel</MenuItem>
              </Menu>
            </>
          )}
          {customActions}
          {onCreate && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onCreate} sx={{ textTransform: 'none' }}>
              Shto t\u00eb re
            </Button>
          )}
        </Stack>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 700, width: 50 }}>#</TableCell>
              {columns.map(col => (
                <TableCell key={col.field_key} sx={{ fontWeight: 700 }}>
                  {col.is_sortable !== false ? (
                    <TableSortLabel
                      active={sortBy === col.field_key}
                      direction={sortBy === col.field_key ? sortDir : 'asc'}
                      onClick={() => handleSort(col.field_key)}
                    >
                      {col.label || col.field?.label || col.field_key}
                    </TableSortLabel>
                  ) : (
                    col.label || col.field?.label || col.field_key
                  )}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 700, width: 120 }}>Veprime</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={columns.length + 2} align="center">
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <InboxIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {searchTerm ? `Asnj\u00eb rezultat p\u00ebr "${searchTerm}"` : 'Asnj\u00eb regjistrim'}
                    </Typography>
                    {!searchTerm && onCreate && (
                      <Button size="small" startIcon={<AddIcon />} onClick={onCreate} sx={{ mt: 1, textTransform: 'none' }}>
                        Shto t\u00eb par\u00ebn
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {records.map((record, idx) => (
              <TableRow key={record.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                <TableCell sx={{ color: 'text.secondary' }}>{(page - 1) * limit + idx + 1}</TableCell>
                {columns.map(col => (
                  <TableCell key={col.field_key}>{formatCellValue(record, col)}</TableCell>
                ))}
                <TableCell>
                  <Stack direction="row" spacing={0.25}>
                    {onView && (
                      <Tooltip title="Shiko detajet">
                        <IconButton size="small" onClick={() => onView(record)} sx={{ color: 'primary.main' }}>
                          <VisibilityIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {onEdit && (
                      <Tooltip title="Ndrysho">
                        <IconButton size="small" onClick={() => onEdit(record)}>
                          <EditIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {onDelete && (
                      <Tooltip title="Fshi">
                        <IconButton size="small" color="error" onClick={() => onDelete(record)}>
                          <DeleteIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Pagination */}
      {pages > 1 && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Faqja {page} nga {pages}
          </Typography>
          <Pagination
            count={pages}
            page={page}
            onChange={(_, p) => onPageChange && onPageChange(p)}
            color="primary"
            size="small"
          />
        </Stack>
      )}
    </Box>
  );
}
